import type { ReactElement } from "react";
import { ICON_ART_SIZE } from "odyssey-shared";
import { logical } from "./metrics.ts";

/**
 * The kit's colour art icon (ADR-111 decisions 12 and 15): one 32 pixel picture of the icon atlas, drawn at k device
 * pixels an art pixel, where k is the largest whole number at which it fits its box at the UI scale. Each box has one
 * CSS length, `--art-48`, `--art-32`, `--art-22`, `--art-16`, `--art-13`, `--art-10` or `--art-8`, k device pixels as CSS pixels, which the overlay root sets beside
 * `--px`; the art's size, the atlas's size and the picture's place in it are whole multiples of that length, so every
 * art pixel is a whole square of device pixels at any device pixel ratio. The art is centred in its room, the element
 * it is drawn in, and clipped to it: the room's side and the art's are both an even number of device pixels in every
 * box the game draws, so the centre is a whole device pixel too.
 *
 * Which is drawn, the art or a glyph, is the page's choice (`game/icons/art.ts`); this draws what it is handed.
 */

/**
 * The boxes art is scaled for, in logical pixels: 22 for a slot inside its edge (a bag or chest slot, the bar's item
 * slot) and an emoji's cell on the emote wheel, 16 for an effect's box inside its ring and a toast's line, and 32 for
 * an emoji's cell at a UI scale where the 22 box holds no art, so an emoji is never left without its picture (ADR-114's
 * note for P2.19-T9, after decision 5.3), 48 for the item card's picture plate inside its ring (ADR-125 decision 5.3), and
 * 16, 13, 12 and 10 for a staff badge on a name plate and beside a name, and 8 for one in a chat line, whose built line is 8
 * tall (ADR-112 decisions 4.3 and 4.5).
 */
export type ArtBox = 48 | 32 | 22 | 16 | 13 | 12 | 10 | 8;

/**
 * One picture of the atlas as the art icon draws it: its key (named on the element for tests and tools), the atlas's
 * address and size in its own pixels, the top left pixel of the 32 by 32 picture in it, and the box it is scaled for.
 */
export interface IconArt {
  readonly key: string;
  readonly url: string;
  readonly width: number;
  readonly height: number;
  readonly x: number;
  readonly y: number;
  readonly box: ArtBox;
}

/**
 * The CSS length of one art pixel in a box.
 *
 * @param box The box.
 * @returns `var(--art-<box>)`.
 * @example
 * artLength(22);
 * // "var(--art-22)"
 */
export function artLength(box: ArtBox): string {
  return `var(--art-${box})`;
}

/**
 * A whole number of art pixels in a box as a CSS length.
 *
 * @param n The number of art pixels, negative to the left or up.
 * @param length The CSS length of one art pixel.
 * @returns `calc(n * <length>)`.
 */
function artPixels(n: number, length: string): string {
  return `calc(${n} * ${length})`;
}

/**
 * The art icon: a room the art is centred in and clipped to, holding the picture.
 *
 * @param props The `art`; the `room`'s side in logical pixels; where the room stands in its containing block (`at`,
 *   logical pixels from its top left), or null for a room in the flow of its parent; and optionally the CSS length of one
 *   art pixel (`unit`), which a place that draws its art at a fraction of a device pixel hands in, `artLength(art.box)` otherwise;
 *   `scaled`, which marks art drawn off the whole device pixels so the sheet may smooth it.
 * @returns The room and its picture.
 * @example
 * <ArtIcon art={art} room={22} at={{ left: 0, top: 0 }} />
 */
export function ArtIcon({ art, room, at, unit, scaled = false }: { readonly art: IconArt; readonly room: number; readonly at: { readonly left: number; readonly top: number } | null; readonly unit?: string; readonly scaled?: boolean }): ReactElement {
  const length = unit ?? artLength(art.box);
  const offset = `calc((${logical(room)} - ${artPixels(ICON_ART_SIZE, length)}) / 2)`;
  return (
    <span className="ot-art-room" data-kit="art-room" aria-hidden="true" style={at === null ? { position: "relative", width: logical(room), height: logical(room) } : { position: "absolute", left: logical(at.left), top: logical(at.top), width: logical(room), height: logical(room) }}>
      <span
        className={scaled ? "ot-art ot-art--scaled" : "ot-art"}
        data-kit="art"
        data-icon={art.key}
        style={{
          left: offset,
          top: offset,
          width: artPixels(ICON_ART_SIZE, length),
          height: artPixels(ICON_ART_SIZE, length),
          backgroundImage: `url(${JSON.stringify(art.url)})`,
          backgroundSize: `${artPixels(art.width, length)} ${artPixels(art.height, length)}`,
          backgroundPosition: `${artPixels(-art.x, length)} ${artPixels(-art.y, length)}`,
        }}
      />
    </span>
  );
}
